import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { Download, Briefcase, GraduationCap } from "lucide-react";
import api, { resolveUrl } from "@/lib/api";

function ResumeSkeleton() {
  return (
    <div className="space-y-6 animate-pulse mt-8">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="glass rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-6 w-1/3 bg-muted rounded" />
            <div className="h-4 w-20 bg-muted rounded" />
          </div>
          <div className="h-4 w-1/4 bg-muted rounded mt-2" />
          <div className="space-y-2 mt-4">
            <div className="h-3.5 w-full bg-muted/60 rounded" />
            <div className="h-3.5 w-5/6 bg-muted/60 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Resume() {
  const { profile } = useOutletContext();
  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/experiences").then((r) => setExperiences(r.data)),
      api.get("/educations").then((r) => setEducations(r.data))
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10" data-testid="resume-page">
      <Seo title="Resume & Experience" description="Experience, work history, and education. Download the full PDF resume." path="/resume" />
      
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Experience</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-light tracking-tighter mt-3 leading-[1.05]">Resume & journey</h1>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-1.5 shrink-0">
            <a href={resolveUrl(profile?.resume_url || "#")} target="_blank" rel="noreferrer" data-testid="resume-download-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-medium hover:opacity-90 transition-opacity">
              <Download className="w-4 h-4" /> Download PDF
            </a>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-2 text-center sm:text-right font-mono">
              Last updated: {profile?.resume_last_updated || "July 2026"}
            </span>
          </div>
        </div>
      </Reveal>

      <div className="mt-16 space-y-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Briefcase className="w-4 h-4" />
          <span className="text-xs uppercase tracking-[0.2em]">Work</span>
        </div>

        {loading ? (
          <ResumeSkeleton />
        ) : (
          experiences.map((e, i) => (
            <Reveal key={e.id || i} delay={i * 0.06}>
              <div className="glass rounded-2xl p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <h3 className="font-heading text-xl">{e.role}</h3>
                  <span className="text-sm font-mono text-muted-foreground">{e.period}</span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">{e.org}</div>
                <ul className="mt-4 space-y-2">
                  {(e.points || []).map((p, j) => (
                    <li key={j} className="text-sm text-muted-foreground leading-relaxed flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-foreground/40 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))
        )}

        <div className="flex items-center gap-2 text-muted-foreground pt-6">
          <GraduationCap className="w-4 h-4" />
          <span className="text-xs uppercase tracking-[0.2em]">Education</span>
        </div>

        {loading ? (
          <div className="glass rounded-2xl p-6 sm:p-8 mt-4 animate-pulse h-28 bg-muted/10" />
        ) : (
          educations.map((ed, i) => (
            <Reveal key={ed.id || i} delay={i * 0.06}>
              <div className="glass rounded-2xl p-6 sm:p-8 mt-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <h3 className="font-heading text-xl">{ed.degree}</h3>
                  <span className="text-sm font-mono text-muted-foreground">{ed.period}</span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">{ed.school}</div>
              </div>
            </Reveal>
          ))
        )}
      </div>
    </div>
  );
}
